import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  IonToolbar,
  IonContent,
  IonPage,
  IonTitle,
  IonMenuButton,
  IonButton,
  IonButtons,
  IonHeader,
  IonList,
  IonListHeader,
  IonItem,
  IonInput,
  IonLoading,
  IonSelect,
  IonSelectOption,
  getConfig,
  useIonToast,
} from '@ionic/react';

import './StorePage.scss';
import { connect } from '../data/connect';
import { Store } from '../models/Store';
import axiosInstance from '../data/axios/axiosInstance';
import StoreList from '../components/StoreList';

interface OwnProps {
  onTabChange: (event: CustomEvent) => void;
}

interface StateProps {
  mode: 'ios' | 'md';
  isAuthenticated: boolean;
}

interface DispatchProps {
}

type StorePageProps = OwnProps & StateProps & DispatchProps;

const StorePage: React.FC<StorePageProps> = ({
  isAuthenticated,
  onTabChange,
}) => {
  const [jancode, setJancode] = useState('');
  const [original, setOriginal] = useState('');
  const [racknum, setRacknum] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [count, setCount] = useState(0);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const rackRef = useRef<HTMLIonInputElement>(null);
  const quantityRef = useRef<HTMLIonSelectElement>(null);
  const [presentToast] = useIonToast();
  const location = useLocation();

  const showToast = (message: string, duration = 3000, focusRef?: React.RefObject<any>) => {
    presentToast({
      message,
      duration,
      position: 'middle',
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
            // Close 押下後にフォーカスを戻す
          if (focusRef) {
            setTimeout(() => {
              if (typeof focusRef.current?.setFocus === 'function') {
                focusRef.current.setFocus();
              } else if (typeof focusRef.current?.open === 'function') {
                focusRef.current.open();
              }
            }, 50);
          }
          },
        },
      ],
    });
  };

  useEffect(() => {
    inputRef.current?.setFocus();
  }, [count]);

  const playBuzzer = () => {
    const buzzer = document.getElementById('buzzer') as HTMLAudioElement;
    if (buzzer) {
      buzzer.play();
    }
  };

  const playRing = () => {
    const ring = document.getElementById('ring') as HTMLAudioElement;
    if (ring) {
      ring.play();
    }
  };

  const handleJancodeChange = (event: CustomEvent) => {
    setJancode((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleOriginalChange = (event: CustomEvent) => {
    setOriginal((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleRacknumChange = (event: CustomEvent) => {
    setRacknum((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleQuantityChange = (event: CustomEvent) => {
    setQuantity(Number((event.target as HTMLInputElement).value));
  };

  const handleJanKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      rackRef.current?.setFocus();
    }
  };

  const handleRackKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      quantityRef.current?.open();
    }
  };

  const handleProcess = async () => {
    if ( jancode === "" ) {
      showToast("【JAN】や【original_num】を入力してください。", 3000, inputRef);
      playBuzzer();
      inputRef.current?.setFocus();
      return;
    }

    if ( racknum === "" ) {
      showToast("棚番を入力してください。", 3000, rackRef);
      playBuzzer();
      return;
    }

    if ( quantity === 0 ) {
      showToast("入庫個数", 3000, quantityRef);
      playBuzzer();
      return;
    }

    setLoading(true);      
    try {
      const response = await axiosInstance.post('/stores/', { jan: jancode, rack_num: racknum, quantity: quantity });
      if (response.status === 200 && response.data.result == "success") {
        setStores(response.data.stores);
        setCount(response.data.stores.length)
        setJancode('');
        setOriginal('');
        setRacknum('');
        setQuantity(0);
        inputRef.current?.setFocus();
        playRing();
      } else {
        showToast("入庫エラー", 3000, inputRef);
        playBuzzer();
      }
      setLoading(false);
    } catch (error) {
      console.error('Error processing stores:', error);
      playBuzzer();
      setLoading(false);
    }
  };

  const handleJancodeClear = () => {
    setJancode("");
    setRacknum("");
    setQuantity(0); 
    inputRef.current?.setFocus();
  }

  const fetchInspections = async () => {
    try {
      const response = await axiosInstance.get('/store');
      if (response.status === 200) {
        setStores(response.data.stores)
        setCount(response.data.stores.length)

        inputRef.current?.setFocus();
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated && location.pathname.includes("store")) {
      fetchInspections();
    }
  }, [isAuthenticated, onTabChange]);

  return (
    <IonPage ref={pageRef} id="store-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>入庫作業</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>
        <audio id="buzzer" src="/assets/error.mp3" preload="auto"></audio>
        <audio id="ring" src="/assets/ok.mp3" preload="auto"></audio>
        <IonList>
          <IonItem>
            <IonInput label="JAN or OGN" ref={inputRef} placeholder="0000000000000" value={jancode} onIonInput={handleJancodeChange} onKeyDown={handleJanKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleJancodeClear}>クリア</IonButton>
          </IonItem>
          <IonItem>
            <IonInput label="棚番" ref={rackRef} value={racknum} onIonInput={handleRacknumChange} onKeyDown={handleRackKeyPress}></IonInput>
          </IonItem>
          <IonItem>
            <IonSelect label="入庫個数" ref={quantityRef} value={quantity} onIonChange={handleQuantityChange}>
              {Array.from({ length: 101 }, (_, i) => (
                <IonSelectOption key={i} value={i}>
                  {i}
                </IonSelectOption>
              ))}
            </IonSelect>
            <IonButton color="primary" expand="full" slot='end' onClick={handleProcess}>完了</IonButton>
          </IonItem>
        </IonList>
        <IonListHeader style={{ fontWeight: 'bold', fontSize: '22px' }}>{"入庫マスタ : " + count + "件"}</IonListHeader>
        <div style={{ overflowY: 'auto', height: 'calc(100% - 110px)' }}>
          <StoreList
            stores={stores}
          />
        </div>
        <IonLoading
          isOpen={loading}
          message={'Loading...'}
          onDidDismiss={() => setLoading(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    mode: getConfig()!.get('mode'),
    isAuthenticated: state.user.isLoggedin,
  }),
  mapDispatchToProps: {
  },
  component: React.memo(StorePage),
});
